import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { trackEvent } from "@/lib/analytics";

const MILESTONES = [25, 50, 75, 100] as const;

/**
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%) and fires Plausible events.
 * Resets on route change. Each milestone fires once per pageview.
 */
export function useScrollDepth() {
  const { pathname } = useLocation();
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    firedRef.current = new Set();

    function handleScroll() {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((window.scrollY / scrollHeight) * 100);

      for (const milestone of MILESTONES) {
        if (pct >= milestone && !firedRef.current.has(milestone)) {
          firedRef.current.add(milestone);
          trackEvent("Scroll_Depth", { depth: `${milestone}%`, page: pathname });
        }
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);
}
